<template>
  <!-- Extracto Inteligente potenciado con IA -->
  <div v-if="extractData" class="extract-container">
    <div class="extract-wrapper">
      <!-- Header con icono y título -->
      <div class="summary-header">
        <div class="d-flex align-items-center">
          <div class="ai-icon me-3">
            <i class="fas fa-robot"></i>
          </div>
          <div class="flex-grow-1">
            <h3 class="mb-1">
              {{ $t("Extracto Inteligente del documento") }}
            </h3>
            <p class="mb-0 text-muted">
              {{ $t("Highlight académico extraído del documento PDF") }}
            </p>
          </div>
          <div class="ai-badge">
            <AIBadge />
          </div>
        </div>
      </div>

      <!-- Contenido del extracto -->
      <div class="row g-3">
        <!-- Temas principales -->
        <div class="col-md-6" v-if="extractData.extract.mainTopics">
          <div class="extract-card-compact">
            <h6 class="fw-bold mb-2">
              <i class="fas fa-list-ul me-2"></i>{{ $t("Temas principales") }}
            </h6>
            <div>
              <div v-for="(topic, index) in formatList(extractData.extract.mainTopics)" :key="index" class="mb-1">
                · {{ topic }}
              </div>
            </div>
          </div>
        </div>

        <!-- Conceptos clave -->
        <div class="col-md-6" v-if="extractData.extract.keyConcepts">
          <div class="extract-card-compact">
            <h6 class="fw-bold mb-2">
              <i class="fas fa-lightbulb me-2"></i>{{ $t("Conceptos clave") }}
            </h6>
            <div style="line-height: 1.8;">
              <span v-for="(concept, index) in formatList(extractData.extract.keyConcepts)" :key="index" class="badge me-2 mb-2" style="background-color: #b8b8b8; color: #4A4A4A; font-weight: 400; padding: 0.5rem 0.8rem;">
                {{ concept }}
              </span>
            </div>
          </div>
        </div>

        <!-- Aplicaciones prácticas -->
        <div class="col-md-6" v-if="extractData.extract.practicalApplications">
          <div class="extract-card-compact">
            <h6 class="fw-bold mb-2">
              <i class="fas fa-tools me-2"></i>{{ $t("Aplicaciones prácticas") }}
            </h6>
            <div>
              <div v-for="(app, index) in formatList(extractData.extract.practicalApplications)" :key="index" class="mb-1">
                · {{ app }}
              </div>
            </div>
          </div>
        </div>

        <!-- Takeaways -->
        <div class="col-md-6" v-if="extractData.extract.takeaways">
          <div class="extract-card-compact">
            <h6 class="fw-bold mb-2">
              <i class="fas fa-star me-2"></i>{{ $t("Ideas clave") }}
            </h6>
            <div>
              <div v-for="(takeaway, index) in formatList(extractData.extract.takeaways)" :key="index" class="mb-1">
                · {{ takeaway }}
              </div>
            </div>
          </div>
        </div>

        <!-- Preguntas clave -->
        <div class="col-md-12" v-if="extractData.extract.keyQuestions">
          <div class="extract-card-compact">
            <h6 class="fw-bold mb-2">
              <i class="fas fa-question-circle me-2"></i>{{ $t("Preguntas clave") }}
            </h6>
            <div>
              <div v-for="(question, index) in formatList(extractData.extract.keyQuestions)" :key="index" class="mb-1">
                · {{ question }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch, onMounted } from 'vue';
import api from "@/api-backend";
import i18n from "@/i18n";
import AIBadge from "@/components/AIBadge.vue";

export default {
  name: 'IntelligentExtractCard',
  components: { AIBadge },
  props: {
    materialId: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const extractData = ref(null);
    const loadingExtract = ref(false);

    // Función para formatear listas separadas por |
    const formatList = (text) => {
      if (!text) return [];
      return text.split('|')
        .map(item => item.trim())
        .filter(item => item.length > 0);
    };

    // Obtener el locale actual de i18n
    const { locale } = i18n.global;

    // Función para obtener el extracto inteligente
    const fetchExtract = async (materialId) => {
      if (!materialId) return;
      
      try {
        loadingExtract.value = true;
        // El ID ya viene con formato 2117_408706 desde el router
        // Pasamos el locale actual (ca, es, en) a la API
        const response = await api.get(`/extract-ia/get/${materialId}?locale=${locale.value}`);
        extractData.value = response.data;
      } catch (error) {
        // Si no existe el extracto (404) o hay error, no mostrar nada
        extractData.value = null;
        console.debug('Extracto no disponible:', error.response?.status || error.message);
      } finally {
        loadingExtract.value = false;
      }
    };

    // Cargar extracto cuando el componente se monta
    onMounted(() => {
      if (props.materialId) {
        fetchExtract(props.materialId);
      }
    });

    // Watcher para cuando cambie el materialId
    watch(() => props.materialId, async (newId) => {
      if (newId) {
        await fetchExtract(newId);
      }
    });

    // Watcher para cuando cambie el idioma (locale)
    watch(locale, async () => {
      if (props.materialId) {
        await fetchExtract(props.materialId);
      }
    });

    return {
      extractData,
      loadingExtract,
      formatList
    };
  }
};
</script>

<style scoped>
.extract-container {
  margin: 3rem 0;
}

.extract-wrapper {
  background-color: #f2f5f6;
  padding: 2rem;
  border: none;
}

.summary-header {
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #d1d1d1;
}

.ai-icon {
  font-size: 2.5rem;
  color: #007BC1;
  background: white;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e0e0e0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.extract-wrapper h3 {
  font-size: 1.75rem;
  font-weight: 500;
  color: #4A4A4A;
  margin: 0;
}

.extract-wrapper .text-muted {
  font-size: 0.95rem;
  color: #636363;
}

.ai-badge .badge {
  font-size: 1rem;
  padding: 0.6rem 1.2rem;
  font-weight: 500;
}

/* Cards compactas al estilo anterior */
.extract-card-compact {
  background-color: white;
  border: 1px solid #e0e0e0;
  padding: 1rem;
  height: 100%;
  color: #636363;
  font-size: 1rem;
}

.extract-card-compact h6 {
  color: #4A4A4A;
  font-size: 1rem;
}

.extract-card-compact h6 i {
  color: #007BC1;
  font-size: 1rem;
}

@media (max-width: 768px) {
  .extract-wrapper {
    padding: 1.5rem;
  }

  .extract-wrapper h3 {
    font-size: 1.5rem;
  }

  .ai-icon {
    width: 50px;
    height: 50px;
    font-size: 2rem;
  }

  .extract-card-compact {
    margin-bottom: 1rem;
    padding: 0.75rem;
    font-size: 1rem;
  }

  .extract-card-compact h6 {
    font-size: 1rem;
  }
}
</style>